/**
 * MockedOverlay — wraps any component to mark it as not-yet-wired (Q4
 * of WORKSHOP_DASHBOARDS.md). Renders children at 40% opacity with a
 * small "Mocked" badge in the top-right. The badge is purely decorative —
 * the `reason` is conveyed to assistive tech via the region's aria-label
 * and to mouse users via the native title tooltip.
 *
 * Used initially on the Corp Events bucket until CORP_EVENTS.md ships.
 *
 * Why not a button? The badge has no onClick — it's a status indicator,
 * not an action. A button there fails the discoverability heuristic
 * ("looks clickable, isn't"). The wrap div is tabbable (tabIndex={0}) so
 * keyboard users can land on it and hear the reason via the aria-label,
 * but the role stays `region` not `button`.
 */
import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

export interface MockedOverlayProps {
  reason: string;
  children: ReactNode;
}

export function MockedOverlay({ reason, children }: MockedOverlayProps) {
  return (
    <div
      className="nb-mocked-wrap"
      role="region"
      aria-label={`Mocked: ${reason}`}
      title={reason}
      tabIndex={0}
    >
      <div className="nb-mocked-pill" aria-hidden="true">
        <Sparkles size={11} strokeWidth={1.75} />
        <span>Mocked</span>
      </div>
      <div className="nb-mocked-children" aria-hidden="true">
        {children}
      </div>
    </div>
  );
}
